import { useAuth0 } from '@auth0/auth0-react';
import { otherColors } from '@ehrTheme/colors';
import { Button } from '@mui/material';
import { Bundle, BundleEntry, DocumentReference } from 'fhir/r4b';
import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIpAddress,
  getPresignedURL,
  getQuestionnaireResponseByLinkId,
  mdyStringFromISOString,
  PAPERWORK_CONSENT_CODE_UNIQUE,
} from 'utils';
import { useGetDocumentReferences } from '../../../shared/stores/appointment/appointment.queries';
import { useAppointmentData } from '../../../shared/stores/appointment/appointment.store';
import { InformationCard } from './InformationCard';

const PdfButton = ({ pdfUrl }: { pdfUrl?: string }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outlined"
      sx={{
        borderColor: otherColors.consentBorder,
        borderRadius: 100,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: 14,
        minWidth: 'max-content',
      }}
      href={pdfUrl || ''}
      target="_blank"
      disabled={!pdfUrl}
    >
      {t('completedForms.getPdf', 'Get PDF')}
    </Button>
  );
};

export const CompletedFormsContainer: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();
  const { patient, appointment, questionnaireResponse } = useAppointmentData();
  const [consentPdfUrl, setConsentPdfUrl] = useState<string | undefined>();
  const [hipaaPdfUrl, setHipaaPdfUrl] = useState<string | undefined>();

  useGetDocumentReferences({ appointmentId: appointment?.id, patientId: patient?.id }, async (data) => {
    const authToken = await getAccessTokenSilently();
    const documentReferenceResources: DocumentReference[] = [];
    const bundleEntries = data.entry;

    bundleEntries?.forEach((bundleEntry: BundleEntry) => {
      const bundleResource = bundleEntry.resource as Bundle;
      bundleResource.entry?.forEach((entry) => {
        const docRefResource = entry.resource as DocumentReference;
        if (docRefResource) {
          documentReferenceResources.push(docRefResource);
        }
      });
    });

    for (const docRef of documentReferenceResources) {
      const isDocRefPaperworkConsent = docRef.type?.coding?.some((coding) => {
        return coding === PAPERWORK_CONSENT_CODE_UNIQUE;
      });

      if (isDocRefPaperworkConsent) {
        for (const content of docRef.content) {
          const title = content.attachment.title;
          const z3Url = content.attachment.url;
          const presignedUrl = z3Url && (await getPresignedURL(z3Url, authToken));

          if (title === 'Consent forms') {
            setConsentPdfUrl(presignedUrl);
          } else if (title === 'HIPAA forms') {
            setHipaaPdfUrl(presignedUrl);
          }
        }
      }
    }
  });

  const hipaaAcknowledgement = getQuestionnaireResponseByLinkId('hipaa-acknowledgement', questionnaireResponse)
    ?.answer?.[0]?.valueBoolean;
  const consentToTreat = getQuestionnaireResponseByLinkId('consent-to-treat', questionnaireResponse)?.answer?.[0]
    ?.valueBoolean;

  const signature = getQuestionnaireResponseByLinkId('signature', questionnaireResponse)?.answer?.[0]?.valueString;
  const fullName = getQuestionnaireResponseByLinkId('full-name', questionnaireResponse)?.answer?.[0]?.valueString;
  const relationship = getQuestionnaireResponseByLinkId('consent-form-signer-relationship', questionnaireResponse)
    ?.answer?.[0]?.valueString;
  const signDate = questionnaireResponse?.authored && mdyStringFromISOString(questionnaireResponse?.authored);
  const ipAddress = getIpAddress(questionnaireResponse);

  return (
    <InformationCard
      title={t('completedForms.title', 'Completed consent forms')}
      fields={[
        {
          label: t('completedForms.hipaa', 'I have reviewed and accept HIPAA Acknowledgement'),
          value: hipaaAcknowledgement
            ? t('completedForms.signed', 'Signed')
            : t('completedForms.notSigned', 'Not signed'),
          button: <PdfButton pdfUrl={hipaaPdfUrl} />,
        },
        {
          label: t(
            'completedForms.consentToTreat',
            'I have reviewed and accept Consent to Treat, Guarantee of Payment & Card on File Agreement'
          ),
          value: consentToTreat ? t('completedForms.signed', 'Signed') : t('completedForms.notSigned', 'Not signed'),
          button: <PdfButton pdfUrl={consentPdfUrl} />,
        },
        {
          label: t('completedForms.signature', 'Signature'),
          value: signature,
        },
        {
          label: t('completedForms.fullName', 'Full name'),
          value: fullName,
        },
        {
          label: t('completedForms.relationship', 'Relationship to the patient'),
          value: relationship,
        },
        {
          label: t('completedForms.date', 'Date'),
          value: signDate,
        },
        {
          label: t('completedForms.ip', 'IP'),
          value: ipAddress,
        },
      ]}
    />
  );
};
